import store from "@/store";
import axios from "axios";
import router from "@/router";
import { tansParams } from "@/utils/ruoyi";
import { goToOfficialWebsite } from "@/utils/domain";
import { clearAuthStorage, isJwtExpired } from "@/utils/auth-token";
import {
  encryptRequest,
  decryptResponse,
  isTimestampValid,
} from "@/utils/crypto";
import {
  API_WORD_ROUTES,
  WHITE_LIST_ROUTES,
  shouldEncrypt,
} from "@/utils/crypto-config";

/**
 * 主要更换线上/测试地址打包时，先到crypto.js更换公钥
 */

//  - 线上
// const baseURL = "https://api-go.skyshinetech.com/api/";

// 测试
const configuredBaseURL = process.env.VUE_APP_API_BASE_URL || "";
const testServerHost = process.env.VUE_APP_TEST_SERVER || "98.130.117.203";

const getApiBaseURL = () => {
  const fallbackBaseURL = `http://${testServerHost}/api/`;

  if (typeof window === "undefined") {
    return configuredBaseURL || fallbackBaseURL;
  }

  const { hostname, protocol } = window.location;
  const isRelativeApiBase = ["/api", "/api/"].includes(configuredBaseURL);

  // 测试服前台是独立静态端口，直接请求 /api 会打到静态站点并返回 405。
  if (isRelativeApiBase && hostname === testServerHost) {
    return `${protocol}//${hostname}/api/`;
  }

  return configuredBaseURL || fallbackBaseURL;
};

const baseURL = getApiBaseURL();
const LOGIN_EXPIRED_MESSAGE = "登录已过期，请重新登录";
let isHandlingLoginExpired = false;

// 创建主请求实例
const request = axios.create({
  baseURL: baseURL,
});

function clearLoginState() {
  clearAuthStorage();
  store.commit("SET_LOGIN", false);
  store.commit("SET_VERIFICATION", "0");
}

function navigateAfterLoginExpired(shouldGoLogin) {
  const currentPath = router.currentRoute?.path || "";
  const isApiWordPage = currentPath.includes("apiWord");

  if (shouldGoLogin) {
    if (isApiWordPage) {
      goToOfficialWebsite("/login?clearCache=true");
      return;
    }

    if (currentPath !== "/login") {
      router.replace("/login").catch(() => {});
    }
    return;
  }

  if (isApiWordPage) {
    goToOfficialWebsite("/?clearCache=true");
    return;
  }

  if (currentPath !== "/") {
    router.replace("/").catch(() => {});
  }
}

function isLoginRequest(url) {
  const path = String(url || "")
    .split("?")[0]
    .replace(/^\/+/, "");
  return path === "login";
}

// 为两个实例都添加相同的拦截器
function setupInterceptors(requestInstance) {
  requestInstance.interceptors.request.use(
    async function (config) {
      // 请求参数处理逻辑
      let url;
      if (config.method === "get" && config.params) {
        url = config.url + "?" + tansParams(config.params);
        url = url.slice(0, -1);
        config.params = {};
        url = url + "&lang=" + config.lang;
        config.url = url;
      } else if (config.method === "get" && config.params === undefined) {
        url = config.url + "?" + "lang=" + config.lang;
        config.url = url;
      }

      const token = localStorage.getItem("token");
      if (token && isJwtExpired(token)) {
        clearLoginState();
        if (
          ["/controlCenter", "/cloudWord", "/apiWord"].some((path) =>
            (router.currentRoute?.path || "").startsWith(path),
          )
        ) {
          navigateAfterLoginExpired(true);
        }
      } else if (token && !isLoginRequest(config.url)) {
        isHandlingLoginExpired = false;
        config.headers.token = token;
        config.headers.Authorization =
          `Bearer ` + token;
      }
      config.headers.lang = store.state.isLang != "zh-CN" ? "en" : "zh";

      if (config.data && config.data.isFormData) {
        const formData = new FormData();
        for (const key in config.data) {
          if (config.data[key] !== undefined && key !== "isFormData") {
            formData.append(key, config.data[key]);
          }
        }
        config.data = formData;
        config.headers["Content-Type"] = "multipart/form-data";
      }

      // API 加密处理（仅对 apiWord 模块的接口加密，且不在白名单中）
      if (
        ["post", "put", "patch", "delete"].includes(
          config.method?.toLowerCase(),
        ) &&
        shouldEncrypt(config.url) &&
        config.data &&
        !config.data.isFormData
      ) {
        try {
          const encrypted = await encryptRequest(config.data);
          // 添加加密相关的请求头
          config.headers["X-Encrypt-Key"] = encrypted.encryptedKey;
          config.headers["X-Nonce"] = encrypted.nonce;
          // 时间戳已经是秒级，直接使用
          config.headers["X-Timestamp"] = encrypted.timestamp.toString();
          // 设置加密后请求的 Content-Type 为 text/plain
          config.headers["Content-Type"] = "text/plain";
          // 将加密后的数据作为请求体
          config.data = encrypted.body;
          // 保存 AES 密钥到 config 中，用于解密响应
          config._cryptoInfo = {
            aesKey: encrypted.aesKey,
          };

          // 设置响应类型为 arraybuffer（接收二进制数据）
          config.responseType = "arraybuffer";
        } catch (error) {
          return Promise.reject(error);
        }
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  requestInstance.interceptors.response.use(
    async function (response) {
      // 如果请求使用了加密，尝试解密响应
      if (response.config._cryptoInfo) {
        try {
          const { aesKey } = response.config._cryptoInfo;

          // 方法 1: 从 Axios 响应头获取
          let responseNonce =
            response.headers["x-nonce"] || response.headers["X-Nonce"];

          // 方法 2: 如果方法 1 失败，尝试从原始 XMLHttpRequest 获取
          if (!responseNonce && response.request) {
            try {
              // 获取原始响应头
              const allHeaders = response.request.getAllResponseHeaders();
              // 查找 X-Nonce（不区分大小写）
              const headerMatch = allHeaders.match(/x-nonce:\s*(.+)/i);
              if (headerMatch && headerMatch[1]) {
                responseNonce = headerMatch[1].trim();
              }
            } catch (err) {
              console.warn("⚠️ 无法从 XMLHttpRequest 获取响应头:", err);
            }
          }

          // 如果响应头中没有 X-Nonce，说明响应未加密，直接使用原始数据
          if (!responseNonce) {
            // 将 arraybuffer 转换为字符串，然后解析为 JSON
            const decoder = new TextDecoder();
            const jsonString = decoder.decode(response.data);
            response.data = JSON.parse(jsonString);
          } else {
            const decryptedData = await decryptResponse(
              response.data,
              aesKey,
              responseNonce,
            );
            // 将解密后的数据替换原始响应数据
            response.data = decryptedData;
          }
        } catch (error) {
          console.error("❌ 解密响应失败:", error);
          // 如果解密失败，尝试将响应当作普通 JSON 处理
          try {
            const decoder = new TextDecoder();
            const jsonString = decoder.decode(response.data);
            response.data = JSON.parse(jsonString);
          } catch (parseError) {
            console.error("❌ 解析响应数据失败:", parseError);
            return Promise.reject(new Error("解密响应失败"));
          }
        }
      }
      if (response.data.code === 200) {
        response.data.code = 0;
      }
      // 登录接口的失败不能清空当前状态；只有受保护接口返回未授权才代表会话过期。
      if (response.data.code === 4 && !isLoginRequest(response.config.url)) {
        if (!isHandlingLoginExpired) {
          isHandlingLoginExpired = true;
          clearLoginState();
          const shouldGoLogin = confirm(LOGIN_EXPIRED_MESSAGE);
          navigateAfterLoginExpired(shouldGoLogin);
        } else {
          clearLoginState();
        }

        return Promise.reject(new Error(LOGIN_EXPIRED_MESSAGE));
      }
      return response.data;
    },
    function (error) {
      if (
        error.response?.status === 401 &&
        !isLoginRequest(error.config?.url)
      ) {
        clearLoginState();
        navigateAfterLoginExpired(true);
      }
      return Promise.reject(error);
    },
  );
}

setupInterceptors(request);

export default {
  request,
  urls: "http://108.165.100.71:10086",
};
